import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Sparkles, Clock, Layers, ArrowRight
} from 'lucide-react';
import { categoriesAPI } from '../utils/api';
import { getArticleImage, handleImageError } from '../utils/imageUtils';

/**
 * Editorial Sidebar
 * Clean layout with 2 sections:
 * 1. Popular Reads (trending articles)
 * 2. Browse Categories
 */
export const Sidebar = ({ trendingArticles = [] }) => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoriesAPI.getAll();
        setCategories(res.data || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  return (
    <aside className="sticky top-24 space-y-6" data-testid="sidebar">
      {/* 1. Popular Reads - Editorial Content */}
      {trendingArticles.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid="popular-reads-widget">
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand-red" />
              <h3 className="font-playfair font-bold text-lg text-gray-900">Popular Reads</h3>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {trendingArticles.slice(0, 5).map((article, index) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="flex gap-3 group"
                data-testid={`popular-read-${index}`}
              >
                <div className="flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden">
                  <img
                    src={getArticleImage(article)}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 smooth-transition"
                    loading="lazy"
                    onError={handleImageError}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 group-hover:text-brand-red smooth-transition line-clamp-2 leading-tight mb-1">
                    {article.title}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    {article.reading_time} min read
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* 2. Browse Categories */}
      {categories.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid="categories-widget">
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-brand-red" />
              <h3 className="font-playfair font-bold text-lg text-gray-900">Browse Categories</h3>
            </div>
          </div>
          <div className="p-4">
            <div className="space-y-2">
              {categories.slice(0, 8).map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.slug}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 group smooth-transition"
                  data-testid={`sidebar-category-${category.slug}`}
                >
                  <span className="font-medium text-gray-900 group-hover:text-brand-red smooth-transition">
                    {category.name}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-brand-red smooth-transition" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Newsletter CTA */}
      <div className="bg-gradient-to-br from-brand-green to-emerald-700 rounded-xl p-6 text-white" data-testid="newsletter-widget">
        <h3 className="font-playfair font-bold text-xl mb-2">Stay Updated</h3>
        <p className="text-emerald-100 text-sm mb-4">
          Get the latest buying guides and deals delivered to your inbox.
        </p>
        <Link 
          to="/contact"
          className="flex items-center justify-center gap-2 bg-white text-brand-green font-bold py-3 rounded-lg hover:bg-gray-100 smooth-transition"
        >
          Subscribe Now
        </Link>
      </div>
    </aside>
  );
};

export default Sidebar;
